import { useState, type ReactNode } from 'react';
import { Button, Checkbox, Input, Select } from '@/components/ui';
import { ORDER_STATUS_FILTER } from '@/features/taxInvoices/lib/taxInvoiceLib';
import type { CashReceiptSearch } from '../api/cashReceiptApi';
import {
  ISSUE_STATUS_FILTER,
  KEYWORD_TYPES,
  TAX_TYPE_FILTER,
  daysAgoKst,
  defaultCashReceiptSearch,
  todayKst,
  useCashReceiptLabels,
} from '../lib/cashReceiptLib';

/**
 * 현금영수증 검색(고도몰 "현금영수증 검색") — 검색어·기간(신청일/처리일)·주문상태·발행상태·발행용도·인증 종류·
 * 과세/면세·금액변경 건·상품가격(발급금액) 범위. 거래번호는 검색어 항목으로 합쳤다.
 */

interface Props {
  initial: CashReceiptSearch;
  onSearch: (q: CashReceiptSearch) => void;
}

const PERIODS: ReadonlyArray<{ days: number; key: string; label: string }> = [
  { days: 0, key: 'today', label: '오늘' },
  { days: 7, key: 'd7', label: '7일' },
  { days: 15, key: 'd15', label: '15일' },
  { days: 30, key: 'm1', label: '1개월' },
  { days: 90, key: 'm3', label: '3개월' },
  { days: 365, key: 'y1', label: '1년' },
];

export const CashReceiptSearchForm = ({ initial, onSearch }: Props) => {
  const L = useCashReceiptLabels();
  const { t } = L;
  const [q, setQ] = useState<CashReceiptSearch>(initial);
  const patch = (p: Partial<CashReceiptSearch>) => setQ((prev) => ({ ...prev, ...p }));
  const toggleStatus = (st: string, on: boolean) =>
    patch({ orderStatuses: on ? [...q.orderStatuses, st] : q.orderStatuses.filter((x) => x !== st) });

  const radios = <K extends keyof CashReceiptSearch>(
    name: K,
    values: ReadonlyArray<string>,
    label: (v: string) => string,
  ): ReactNode => (
    <div className="flex flex-wrap items-center gap-4" data-testid={`cash-receipt-filter-${String(name)}`}>
      {values.map((v) => (
        <label key={v} className="inline-flex items-center gap-1.5">
          <input
            type="radio"
            name={`cash-receipt-${String(name)}`}
            checked={q[name] === v}
            onChange={() => patch({ [name]: v } as Partial<CashReceiptSearch>)}
          />
          {label(v)}
        </label>
      ))}
    </div>
  );

  const labelCls = 'text-[color:var(--content-muted)]';

  return (
    <form
      className="flex flex-col gap-3 rounded-lg p-4 text-sm"
      style={{ background: 'var(--surface-raised)', border: '1px solid var(--divider-strong)' }}
      onSubmit={(e) => {
        e.preventDefault();
        onSearch(q);
      }}
      data-testid="cash-receipt-search"
    >
      <div className="grid grid-cols-[100px_1fr] items-center gap-x-3 gap-y-3">
        <span className={labelCls}>{t('orders.cashReceiptAdmin.search.keyword', '검색어')}</span>
        <div className="flex flex-wrap gap-2">
          <Select
            className="w-36"
            value={q.keywordType}
            onChange={(e) => patch({ keywordType: e.target.value as CashReceiptSearch['keywordType'] })}
            aria-label={t('orders.cashReceiptAdmin.search.keywordType', '검색 항목')}
          >
            {KEYWORD_TYPES.map((k) => (
              <option key={k} value={k}>
                {L.keywordType(k)}
              </option>
            ))}
          </Select>
          <Input
            className="max-w-xs"
            value={q.keyword}
            onChange={(e) => patch({ keyword: e.target.value })}
            placeholder={t('orders.cashReceiptAdmin.search.keywordPlaceholder', '검색어 전체를 정확히 입력하세요.')}
            aria-label={t('orders.cashReceiptAdmin.search.keyword', '검색어')}
            data-testid="cash-receipt-keyword"
          />
        </div>

        <span className={labelCls}>{t('orders.cashReceiptAdmin.search.period', '기간검색')}</span>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            className="w-28"
            value={q.dateType}
            onChange={(e) => patch({ dateType: e.target.value as CashReceiptSearch['dateType'] })}
            aria-label={t('orders.cashReceiptAdmin.search.dateType', '기간 기준')}
          >
            <option value="REQUESTED">{t('orders.cashReceiptAdmin.search.requestedAt', '신청일')}</option>
            <option value="PROCESSED">{t('orders.cashReceiptAdmin.search.processedAt', '처리일')}</option>
          </Select>
          <Input type="date" className="w-40" value={q.from} onChange={(e) => patch({ from: e.target.value })} aria-label={t('orders.cashReceiptAdmin.search.from', '시작일')} />
          <span>~</span>
          <Input type="date" className="w-40" value={q.to} onChange={(e) => patch({ to: e.target.value })} aria-label={t('orders.cashReceiptAdmin.search.to', '종료일')} />
          {PERIODS.map((p) => (
            <Button key={p.key} type="button" size="sm" variant="ghost" onClick={() => patch({ from: daysAgoKst(p.days), to: todayKst() })}>
              {t(`orders.cashReceiptAdmin.search.period_${p.key}`, p.label)}
            </Button>
          ))}
        </div>

        <span className={labelCls}>{t('orders.cashReceiptAdmin.search.orderStatus', '주문상태')}</span>
        <div className="flex flex-wrap gap-x-4 gap-y-1" data-testid="cash-receipt-order-status">
          {ORDER_STATUS_FILTER.map((st) => (
            <label key={st} className="inline-flex items-center gap-1.5">
              <Checkbox checked={q.orderStatuses.includes(st)} onCheckedChange={(v) => toggleStatus(st, v === true)} aria-label={L.orderStatus(st)} />
              {L.orderStatus(st)}
            </label>
          ))}
        </div>

        <span className={labelCls}>{t('orders.cashReceiptAdmin.search.issueStatus', '발행상태')}</span>
        {radios('issueStatus', ISSUE_STATUS_FILTER, L.issueStatus)}

        <span className={labelCls}>{t('orders.cashReceiptAdmin.search.receiptType', '발행용도')}</span>
        {radios('receiptType', ['ALL', 'INCOME_DEDUCTION', 'EXPENSE_PROOF'], L.receiptType)}

        <span className={labelCls}>{t('orders.cashReceiptAdmin.search.identityType', '인증 종류')}</span>
        {radios('identityType', ['ALL', 'BUSINESS_NO', 'PHONE'], L.identityType)}

        <span className={labelCls}>{t('orders.cashReceiptAdmin.search.taxType', '과세/면세')}</span>
        <div className="flex flex-wrap items-center gap-6">
          {radios('taxType', TAX_TYPE_FILTER, L.taxType)}
          <label className="inline-flex items-center gap-1.5">
            <Checkbox checked={q.amountChangedOnly} onCheckedChange={(v) => patch({ amountChangedOnly: v === true })} data-testid="cash-receipt-amount-changed" />
            {t('orders.cashReceiptAdmin.search.amountChangedOnly', '발급금액 변경건만 보기')}
          </label>
        </div>

        <span className={labelCls}>{t('orders.cashReceiptAdmin.search.amount', '발급금액')}</span>
        <div className="flex flex-wrap items-center gap-2">
          <Input className="w-36" inputMode="numeric" value={q.minAmount} onChange={(e) => patch({ minAmount: e.target.value.replace(/\D/g, '') })} aria-label={t('orders.cashReceiptAdmin.search.minAmount', '최소 금액')} />
          <span>~</span>
          <Input className="w-36" inputMode="numeric" value={q.maxAmount} onChange={(e) => patch({ maxAmount: e.target.value.replace(/\D/g, '') })} aria-label={t('orders.cashReceiptAdmin.search.maxAmount', '최대 금액')} />
        </div>
      </div>
      <div className="flex justify-center gap-2">
        <Button type="submit" size="sm" variant="primary" data-testid="cash-receipt-search-submit">
          {t('orders.cashReceiptAdmin.search.submit', '검색')}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => {
            const d = defaultCashReceiptSearch();
            setQ(d);
            onSearch(d);
          }}
        >
          {t('orders.cashReceiptAdmin.search.reset', '초기화')}
        </Button>
      </div>
    </form>
  );
};
